import type { DateValue, RangeValue } from '@heroui/react';
import { EProductType, ESubSource } from '@quesmed/types/models';
import { ESubType } from '@quesmed/types/resolvers/query/admin';
import { create } from 'zustand';
import { EProductCategory } from '@/config';
import type { OmitFunctions } from '@/types';
import { createSelectors } from './selector';

interface TimeEarningsState {
  subType: ESubType;
  source: ESubSource;
  universityId: number | null;
  searchText: string;
  productIds: EProductType[];
  year: number | null;
  productCategory: EProductCategory;
  useNetEarning: boolean;
  isFilterDrawerOpen: boolean;
  isLoading: boolean;
  range: { from: Date | null | number; to: Date | null | number };
  dateRange: RangeValue<DateValue> | undefined;

  setSubType: (subType: ESubType) => void;
  setSource: (source: ESubSource) => void;
  setUniversityId: (universityId: number | null) => void;
  setSearchText: (searchText: string) => void;
  setProductIds: (productIds: EProductType[]) => void;
  setYear: (year: number | null) => void;
  setProductCategory: (productCategory: EProductCategory) => void;
  setUseNetEarning: (useNetEarning: boolean) => void;
  setIsFilterDrawerOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setRange: (range: TimeEarningsState['range']) => void;
  setDateRange: (dateRange: TimeEarningsState['dateRange']) => void;
  reset: () => void;
}

const initialState: OmitFunctions<TimeEarningsState> = {
  subType: ESubType.ALL,
  source: ESubSource.STRIPE,
  universityId: null,
  searchText: '',
  productIds: [EProductType.ALL],
  year: 0,
  productCategory: EProductCategory.ALL,
  useNetEarning: true,
  range: { from: null, to: null },
  dateRange: undefined,
  isFilterDrawerOpen: false,
  isLoading: false,
};

export const earningsState = create<TimeEarningsState>()((set) => ({
  ...initialState,
  setSubType: (subType) => set({ subType }),
  setSource: (source) => set({ source }),
  setUniversityId: (universityId) => set({ universityId }),
  setSearchText: (searchText) => set({ searchText }),
  setProductIds: (productIds) => set({ productIds }),
  setYear: (year) => set({ year }),
  setProductCategory: (productCategory) => set({ productCategory }),
  setUseNetEarning: (useNetEarning) => set({ useNetEarning }),
  setIsFilterDrawerOpen: (isOpen) => set({ isFilterDrawerOpen: isOpen }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setRange: (range) => set({ range }),
  setDateRange: (dateRange) => set({ dateRange }),
  reset: () => set(initialState),
}));

export const useTimeEarningsState = () => earningsState((state) => state);
export const useTimeEarnings = createSelectors(earningsState);
